import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ArrowLeft, Home } from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { useCursorContext } from '../../hooks/useCursorContext';
import { SEOHead } from '../seo/SEOHead';

export function NotFoundPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const numbersRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the 404 numbers with glitch effect
      const numbers = numbersRef.current?.querySelectorAll('.number');
      if (numbers) {
        // Initial reveal
        gsap.fromTo(
          numbers,
          { y: 100, opacity: 0, rotationX: -90 },
          {
            y: 0,
            opacity: 1,
            rotationX: 0,
            duration: 1,
            stagger: 0.15,
            ease: 'back.out(1.7)',
          }
        );

        // Floating animation
        numbers.forEach((num, i) => {
          gsap.to(num, {
            y: -10 + i * 5,
            duration: 2 + i * 0.3,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: i * 0.2,
          });
        });
      }

      // Text reveal
      const textElements = textRef.current?.querySelectorAll('.reveal');
      if (textElements) {
        gsap.fromTo(
          textElements,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power2.out',
            delay: 0.5,
          }
        );
      }

      // Decorative orbs floating
      const orbs = containerRef.current?.querySelectorAll('.orb');
      if (orbs) {
        orbs.forEach((orb, i) => {
          gsap.to(orb, {
            x: Math.random() * 40 - 20,
            y: Math.random() * 40 - 20,
            scale: 0.9 + Math.random() * 0.3,
            duration: 4 + i,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });
        });
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <PageTransition>
      <SEOHead
        title="404 - Seite nicht gefunden"
        description="Die gesuchte Seite existiert nicht oder wurde verschoben."
        path="/404"
      />

      <div
        ref={containerRef}
        className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-8 relative overflow-hidden"
      >
        {/* Decorative orbs */}
        <div className="orb absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="orb absolute bottom-1/4 right-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="orb absolute top-1/2 right-1/3 w-48 h-48 bg-white/5 rounded-full blur-2xl" />

        <div className="text-center relative z-10">
          {/* 404 Numbers */}
          <div
            ref={numbersRef}
            className="flex items-center justify-center gap-4 md:gap-8 mb-12"
            style={{ perspective: '1000px' }}
          >
            <span className="number text-[120px] md:text-[200px] lg:text-[280px] font-light text-white/10 select-none leading-none">
              4
            </span>
            <span className="number text-[120px] md:text-[200px] lg:text-[280px] font-light text-emerald-500/30 select-none leading-none">
              0
            </span>
            <span className="number text-[120px] md:text-[200px] lg:text-[280px] font-light text-white/10 select-none leading-none">
              4
            </span>
          </div>

          {/* Text content */}
          <div ref={textRef} className="space-y-6">
            <h1 className="reveal text-2xl md:text-3xl font-light text-white">
              Seite nicht gefunden
            </h1>
            <p className="reveal text-white/40 max-w-md mx-auto">
              Die gesuchte Seite existiert nicht oder wurde verschoben. Keine
              Sorge, du findest deinen Weg zurueck.
            </p>

            {/* CTAs */}
            <div className="reveal flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <Link
                to="/"
                className="group flex items-center gap-3 px-6 py-3 bg-emerald-500 text-black rounded-full font-medium hover:bg-emerald-400 transition-all"
                onMouseEnter={() => setCursorState('hovering')}
                onMouseLeave={() => setCursorState('default')}
              >
                <Home className="w-4 h-4" />
                <span>Zur Startseite</span>
              </Link>
              <button
                onClick={() => window.history.back()}
                className="group flex items-center gap-3 px-6 py-3 border border-white/10 text-white/60 rounded-full hover:border-emerald-500/50 hover:text-white transition-all"
                onMouseEnter={() => setCursorState('hovering')}
                onMouseLeave={() => setCursorState('default')}
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span>Zurueck</span>
              </button>
            </div>
          </div>

          {/* Decorative line */}
          <div className="reveal mt-16 flex items-center justify-center gap-4">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-emerald-500/50" />
            <span className="text-xs text-white/20 tracking-widest uppercase">Error 404</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-emerald-500/50" />
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
